import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { ScopeOverviewService } from '@app/features/scope-overview/service/scope-overview.service'
import { ScopeVersion } from '@core/model/scope-version'
import { Privilege } from '@core/model/enums/privilege.enum'
import { authorisedAsChildCompany, untilDestroyed } from '@shared/utils/utils'
import { User } from '@core/model/user.model'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { ScopeTeamMember } from '@core/model/scope-team.model'
import { UserRoleLevel } from '@core/model/enums/user-role-level.enum'
import { MatDialog } from '@angular/material/dialog'
import { ModalConfig } from '@core/model/modal-config.model'
import { FormControl } from '@angular/forms'
import { CdnConfig } from '@core/model/cdn-config.model'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { filter } from 'rxjs'
import { AuthService } from '@core/service/auth.service'

@Injectable({ providedIn: 'root' })
export class ScopeTeamService {
  get currentUser() {
    return this.authService.loggedInUser
  }

  constructor(
    private store: Store,
    private scopeOverviewService: ScopeOverviewService,
    private dialog: MatDialog,
    private authService: AuthService
  ) {
    this.destroy$ = untilDestroyed()

    this.store
      .select(ScopeOverviewSelectors.selectTeamError)
      .pipe(filter((error) => !!error))
      .subscribe((error) => {
        console.error(error)
      })
  }

  filterMembers(agencyUser: User[], existingTeamMembers: ScopeTeamMember[], accessRoleRequired: UserRoleLevel) {
    return agencyUser.filter((user) => {
      const existingMember = existingTeamMembers.find((m) => {
        return m.user.id === user.id
      })
      return !existingMember && user.hasAccessRole(accessRoleRequired)
    })
  }

  filterAvailableMembers(availableMembers: User[], existingMembers: ScopeTeamMember[]) {
    const allMembers = availableMembers
    if (authorisedAsChildCompany()) {
      if (!allMembers.find((i) => i.id == this.authService.loggedInUser!.id)) {
        allMembers.push(this.authService.loggedInUser!)
      }
    }

    return this.filterMembers(availableMembers, existingMembers, UserRoleLevel.MEMBER)
  }

  showAddCollaboratorModal(currentScope: ScopeVersion, cdnConfig: CdnConfig) {
    this.showAddMemberModal(
      currentScope,
      Privilege.SCOPE__SUBMIT,
      currentScope.identity.team.collaborators,
      cdnConfig,
      'Add Collaborator',
      ScopeOverviewActions.addScopeCollaborator
    )
  }

  showAddReviewerModal(currentScope: ScopeVersion, cdnConfig: CdnConfig) {
    this.showAddMemberModal(
      currentScope,
      Privilege.SCOPE__REVIEW,
      currentScope.identity.team.reviewers,
      cdnConfig,
      'Add Reviewer',
      ScopeOverviewActions.addScopeReviewer
    )
  }

  showAddApproverModal(currentScope: ScopeVersion, cdnConfig: CdnConfig) {
    this.showAddMemberModal(
      currentScope,
      Privilege.SCOPE__APPROVE,
      currentScope.identity.team.approvers,
      cdnConfig,
      'Add Approver',
      ScopeOverviewActions.addScopeApprover
    )
  }

  showAddTraffickerModal(currentScope: ScopeVersion, cdnConfig: CdnConfig) {
    this.showAddMemberModal(
      currentScope,
      Privilege.SCOPE__TRAFFIC,
      currentScope.identity.team.traffickers,
      cdnConfig,
      'Add Trafficker',
      ScopeOverviewActions.addScopeTrafficker
    )
  }

  private showAddMemberModal(
    currentScope: ScopeVersion,
    privilege: Privilege,
    existingMembers: ScopeTeamMember[],
    cdnConfig: CdnConfig,
    title: string,
    confirmAction: any
  ) {
    this.scopeOverviewService
      .getAvailableScopeTeamMembersWithPrivilege(currentScope.identity.id, privilege)
      .pipe(this.destroy$())
      .subscribe((members: User[]) => {
        const availableMembers = this.filterAvailableMembers(members, existingMembers)

        const dialog = this.dialog.open(ScopeUiModalComponent, {
          data: {
            title,
            body: `Invite to ${currentScope.name}`,
            confirmText: 'Add Users',
            confirmCallback: (_form: FormControl, selections: User[]) => {
              dialog.close()
              const memberIds = selections.map((m) => m.id)
              this.store.dispatch(confirmAction({ scopeId: currentScope.identity.id, memberIds }))
            },
            inputs: [
              {
                name: 'members',
                control: new FormControl(''),
                type: 'autocomplete',
                label: 'Member',
                options: availableMembers,
                placeholder: 'Find member',
                multiselect: true,
                optionDetailsFn: (item: User) => {
                  const isParent =
                    this.currentUser.company.parentCompany &&
                    this.currentUser.company.parentCompany.id === item.company.id
                  return isParent ? `(Parent: ${item.company.name})` : ''
                },
              },
            ],
            isFormValid: (_form: FormControl, selections: any[]) => !!selections.length,
            isMemberModal: true,
          },
        })

        dialog.componentInstance.cdnConfig = cdnConfig!
      })
  }

  updateTeamMember(member: ScopeTeamMember, currentScope: ScopeVersion) {
    this.store.dispatch(ScopeOverviewActions.updateTeamMember({ scopeId: currentScope.identity.id, member }))
  }

  deleteCollaborator(member: ScopeTeamMember, currentScope: ScopeVersion) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete Collaborator`,
        `Are you sure want to delete ${member.user.getFullname()} as a collaborator?`,
        'Delete',
        undefined,
        () => {
          dialog.close()

          this.store.dispatch(
            ScopeOverviewActions.removeScopeCollaborator({ scopeId: currentScope.identity.id, member: member })
          )
        },
        undefined
      ),
    })
  }

  deleteReviewer(member: ScopeTeamMember, currentScope: ScopeVersion) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete Reviewer`,
        `Are you sure want to delete ${member.user.getFullname()} as a reviewer?`,
        'Delete',
        undefined,
        () => {
          dialog.close()

          this.store.dispatch(
            ScopeOverviewActions.removeScopeReviewer({ scopeId: currentScope.identity.id, member: member })
          )
        },
        undefined
      ),
    })
  }

  deleteApprover(member: ScopeTeamMember, currentScope: ScopeVersion) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete Approver`,
        `Are you sure want to delete ${member.user.getFullname()} as an approver?`,
        'Delete',
        undefined,
        () => {
          dialog.close()

          this.store.dispatch(
            ScopeOverviewActions.removeScopeApprover({ scopeId: currentScope.identity.id, member: member })
          )
        },
        undefined
      ),
    })
  }

  deleteTrafficker(member: ScopeTeamMember, currentScope: ScopeVersion) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete Trafficker`,
        `Are you sure want to delete ${member.user.getFullname()} as a trafficker?`,
        'Delete',
        undefined,
        () => {
          dialog.close()

          this.store.dispatch(
            ScopeOverviewActions.removeScopeTrafficker({ scopeId: currentScope.identity.id, member: member })
          )
        },
        undefined
      ),
    })
  }

  private readonly destroy$
}
